/**
 * Program Fee 1066 — KUK Fee Structure.
 *
 * Student-type tabs over a row of plan cards, each a headline amount (optionally split
 * into a first-semester and a later-semester figure), an additional-fee list, an
 * optional badge and a Get Started CTA. Ported from kuk-campus-portal
 * `components/Degree/FeePlan.tsx`.
 *
 * DATA — the Program Fee 2 JSON store, unchanged:
 *   one CATEGORY  = one plan card; `tab_group` is the student type it sits under
 *   its ROWS      = that card's amounts — `is_total` on = a headline figure (up to
 *                   two per card, which is v1's "1st semester" / "2nd sem onwards"
 *                   split); `is_total` off = an additional-fee line with a tick
 *   `metadata_1`  = the footnote under the cards, matched to a tab by its `label`
 *
 * Amounts are free text and print as stored, so an editor writes "₹2,18,990" — or
 * "On request" — and gets exactly that.
 *
 * `program_fee_v2` has no `variant` ENUM column (the variant lives on
 * `page_component.variant`), so the variant needs no migration; it is routed to
 * `ProgramFeeV2Service` through the controllers' variant service map.
 */

export const PROGRAM_FEE_1066_CONSTANTS = {
  PROGRAM_FEE_FIELDS_CONFIG: [
    {
      fieldName: "title_1",
      label: "Heading",
      value: "Fee Structure",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Fee Structure",
      required: false,
      defaultValue: "Fee Structure",
    },
    {
      fieldName: "title_1_color",
      label: "Heading + card copy colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "gradient_color_1",
      label: "Heading rule colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      description: "The short accent bar under the heading.",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "background_color",
      label: "Section background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "table_gradient_from",
      label: "Default card surface",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      description: "A card that sets its own background colour overrides this.",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "table_gradient_to",
      label: "Card badge fill",
      value: "#FFCF88",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFCF88",
      required: false,
      defaultValue: "#FFCF88",
    },
    {
      fieldName: "title_2_gradient_from",
      label: "Selected student-type pill + tick colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "title_2_gradient_to",
      label: "Amount caption colour",
      value: "#9C9C9C",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9C9C9C",
      required: false,
      defaultValue: "#9C9C9C",
    },
    {
      fieldName: "metadata_title",
      label: "Additional-fee list heading",
      value: "Additional Fee:",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Additional Fee:",
      required: false,
      defaultValue: "Additional Fee:",
    },
    {
      fieldName: "metadata_title_color",
      label: "Additional-fee heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "cta_text",
      label: "Card CTA text (opens the lead form popup)",
      value: "Get Started",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Get Started",
      required: false,
      defaultValue: "Get Started",
    },
    {
      fieldName: "cta_text_color",
      label: "Card CTA label colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "cta_gradient_from",
      label: "Card CTA fill + student-type switch track",
      value: "#F3F3F3",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#F3F3F3",
      required: false,
      defaultValue: "#F3F3F3",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  // Unused by this variant — the reference has no "What You Get" list.
  METADATA_FIELDS_CONFIG: [],

  // ─── Per-tab footnote, matched to a card's Tab Group by Label ───────────
  METADATA_1_FIELDS_CONFIG: [
    {
      header: "Student type (must match a card's Tab Group)",
      fieldName: "label",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Indian Students",
      required: true,
    },
    {
      header: "Footnote",
      fieldName: "description",
      value: "",
      valueType: "string",
      filedType: "RICH_TEXT_EDITOR",
      required: false,
    },
    {
      header: "Footnote colour",
      fieldName: "text_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Blank uses the heading colour",
      required: false,
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 0,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1",
      required: true,
      defaultValue: 0,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Active/Inactive",
      required: false,
      defaultValue: true,
    },
  ],

  // ─── Per-card fields (one category = one plan card) ─────────────────────
  CATEGORY_FIELDS_CONFIG: [
    {
      fieldName: "tab_group",
      label: "Student type (the tab this card sits under)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Indian Students",
      description: "Cards sharing a value share a tab. The switch hides itself when there is only one.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "badge_text",
      label: "Badge (top-right of the card)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Popular",
      description: "Blank hides the badge.",
      required: false,
      defaultValue: "",
    },
  ],

  // ─── Amount lines inside each card ──────────────────────────────────────
  CATEGORY_METADATA_FIELDS_CONFIG: [
    {
      header: "Caption / line label",
      fieldName: "label",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 1st semester, or Program fee",
      required: false,
    },
    {
      header: "Amount",
      fieldName: "value",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. ₹56,990",
      required: false,
    },
    {
      header: "Headline figure",
      fieldName: "is_total",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "On = the big number; off = an additional-fee line",
      required: false,
      defaultValue: false,
    },
    {
      header: "Caption colour",
      fieldName: "text_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Blank uses the section's",
      required: false,
      defaultValue: "",
    },
    {
      header: "Amount colour",
      fieldName: "value_text_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "Blank uses the card's copy colour",
      required: false,
      defaultValue: "",
    },
    {
      header: "Amount font size",
      fieldName: "value_font_size",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 36px",
      required: false,
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 0,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1",
      required: true,
      defaultValue: 0,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Active/Inactive",
      required: false,
      defaultValue: true,
    },
  ],
};
